/**
 * What an image tool accepts as INPUT — one list, so the file dialog, the drop
 * zone and the "that is not a picture" check cannot drift apart.
 *
 * Everything here reaches a canvas: the browser decodes most of it, and the
 * three it cannot (HEIC, TIFF, SVG) are decoded first by `toDisplayableBlob`
 * (see heicDisplay.js) — that is what a tool must call before drawing a file.
 */

/** MIME types, including the aliases Windows and Android pickers report. */
const INPUT_MIME_TYPES = [
    'image/jpeg',
    'image/pjpeg',
    'image/png',
    'image/apng',
    'image/webp',
    'image/gif',
    'image/bmp',
    'image/x-ms-bmp',
    'image/avif',
    'image/heic',
    'image/heif',
    'image/heic-sequence',
    'image/heif-sequence',
    'image/tiff',
    'image/x-tiff',
    'image/svg+xml',
    'image/x-icon',
    'image/vnd.microsoft.icon',
];

/**
 * Extensions as well as types: Chrome on Windows reports an empty `file.type`
 * for .heic and .avif, and a picker that offers "all files" hands over whatever
 * the name says.
 */
const INPUT_EXTENSIONS = [
    '.jpg', '.jpeg', '.jpe', '.jfif', '.pjp',
    '.png', '.apng',
    '.webp',
    '.gif',
    '.bmp', '.dib',
    '.avif',
    '.heic', '.heif', '.hif',
    '.tif', '.tiff',
    '.svg',
    '.ico',
];

/** For an `<input type="file" accept>` / a drop zone. */
export const IMAGE_INPUT_ACCEPT = [...INPUT_MIME_TYPES, ...INPUT_EXTENSIONS].join(',');

const INPUT_EXT_RE = new RegExp(`(${INPUT_EXTENSIONS.map((ext) => `\\${ext}`).join('|')})$`, 'i');

/**
 * Whether a dropped or picked file is a picture a tool can open. The type is
 * trusted when there is one (`image/…`), the name when there is not.
 */
export function isSupportedImageFile(file) {
    if (!file) return false;
    const type = (file.type || '').toLowerCase();
    if (type.startsWith('image/')) return true;
    return INPUT_EXT_RE.test(file.name || '');
}
